import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";

interface WindowResizeContextValues {
  windowWidth: number;
  windowHeight: number;
}

export const WindowResizeContext = createContext<WindowResizeContextValues>({
  windowWidth: 0,
  windowHeight: 0,
});

const WindowResizeContextProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [windowWidth, setWindowWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);
  const [_isPending, startTransition] = useTransition();

  const handleWindowResize = (e: Event) => {
    const target = e.target as Window;
    const innerWidth = target.innerWidth;
    const innerHeight = target.innerHeight;
    startTransition(() => {
      setWindowWidth(innerWidth);
      setWindowHeight(innerHeight);
    });
  };

  // Establish the window dimensions once the context is available in a browser.
  useEffect(() => {
    startTransition(() => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    });
  }, []);

  // Add the event listener for window resizes using the handler that updates the context state.
  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const value = useMemo(
    () => ({ windowWidth, windowHeight }),
    [windowWidth, windowHeight],
  );

  return (
    <WindowResizeContext.Provider value={value}>
      {children}
    </WindowResizeContext.Provider>
  );
};

export const wrapWithWindowResizeContext = ({
  element,
}: {
  element: ReactNode;
}) => {
  return <WindowResizeContextProvider>{element}</WindowResizeContextProvider>;
};

export default WindowResizeContextProvider;
